import React, { createContext, useContext, useEffect, useReducer, useCallback } from 'react';
import { useProvider } from '~/Hooks';

export enum FilterType {
  EVENT_GROUP_FILTER = 'EVENT_GROUP_FILTER',
  BOOKING_EVENT_GROUP_FILTER = 'BOOKING_EVENT_GROUP_FILTER',
  CALENDAR_FILTER = 'CALENDAR_FILTER',
  CHECKIN_FILTER = 'CHECKIN_FILTER',
}

export interface ProviderContextProviderProps {
  children: React.ReactNode;
}

export type ProviderFiltersContextType = ReturnType<typeof useProviderFiltersContext>;

const ProviderFiltersContext = createContext<ProviderFiltersContextType>(
  {} as ProviderFiltersContextType,
);

const CLEAR_FILTERS = 'CLEAR_FILTERS';

interface IFilterAction {
  type: FilterType | typeof CLEAR_FILTERS;
  payload?: any;
}

const initialFilters = {
  [FilterType.EVENT_GROUP_FILTER]: {},
  [FilterType.BOOKING_EVENT_GROUP_FILTER]: {},
  [FilterType.CALENDAR_FILTER]: {},
  [FilterType.CHECKIN_FILTER]: {},
};

const reducer = (state: typeof initialFilters, action: IFilterAction) => {
  switch (action.type) {
    case CLEAR_FILTERS:
      return { ...initialFilters };
    case FilterType.EVENT_GROUP_FILTER:
      return { ...state, [FilterType.EVENT_GROUP_FILTER]: action.payload };
    case FilterType.BOOKING_EVENT_GROUP_FILTER:
      return { ...state, [FilterType.BOOKING_EVENT_GROUP_FILTER]: action.payload };
    case FilterType.CALENDAR_FILTER:
      return { ...state, [FilterType.CALENDAR_FILTER]: action.payload };
    case FilterType.CHECKIN_FILTER:
      return { ...state, [FilterType.CHECKIN_FILTER]: action.payload };
    default:
      return state;
  }
};

export const useProviderFiltersContext = () => {
  const [filters, dispatch] = useReducer(reducer, initialFilters);
  const { currentProvider } = useProvider();

  useEffect(() => {
    dispatch({ type: CLEAR_FILTERS });
  }, [currentProvider]);

  return {
    filters,
    dispatch,
  };
};

const ProviderFiltersContextWrapper = (props: ProviderContextProviderProps) => {
  const store = useProviderFiltersContext();
  const { children } = props;

  return (
    <ProviderFiltersContext.Provider value={store}>{children}</ProviderFiltersContext.Provider>
  );
};

const useProviderFilters = ({ filterType }: { filterType: FilterType }) => {
  const { filters, dispatch } = useContext(ProviderFiltersContext);
  const updateFilters = useCallback(
    (newFilters: { [key: string]: string | string[] }) =>
      dispatch({ type: filterType, payload: newFilters }),
    [],
  );

  return {
    appliedFilters: filters[filterType],
    updateFilters,
  };
};

export { ProviderFiltersContextWrapper, useProviderFilters };
